import type React from 'react'
import { useCallback } from 'react'
import type { GaFormEventTypeModel, GaFormSubmitModel } from '../types'
import type { GaFormProps } from './GaForm'

interface Props extends GaFormProps {
  formRef: React.RefObject<HTMLFormElement>
  pushToDataLayer: (model: GaFormEventTypeModel) => void
}

/**
 * Hook to handle form submission and push analytics data to GTM.
 *
 * NOTE: If you see objects with undefined form field values in the dataLayer without a
 * gtm.uniqueEventId, this is caused by GTM container configuration (tags/variables),
 * NOT this code. The application correctly sends form events with proper values.
 * Check GTM container for misconfigured tags that fire on form events.
 */

export const useFormSubmit = (props: Props) => {
  const { onSubmit, formRef, pushToDataLayer } = props

  const resolveContent = (
    getter?: () => string | null,
    fallback?: string | null,
  ) => (getter ? (getter() ?? null) : (fallback ?? null))

  const getFormLength = useCallback(() => {
    if (!formRef.current) return 0
    return formRef.current.querySelectorAll('input, textarea, select').length
  }, [])

  const buildModel = useCallback((): GaFormSubmitModel => {
    const formLength = getFormLength()
    const formContent = resolveContent(props.getFormContent, props.formContent)
    const formContent2 = resolveContent(
      props.getFormContent2,
      props.formContent2,
    )
    const formAdditionalContent = resolveContent(
      props.getFormAdditionalContent,
      props.formAdditionalContent,
    )

    return {
      formName: props.formName,
      formId: props.formId,
      formLocation: props.formLocation,
      formDestination: props.formDestination,
      formLength: formLength,
      event: 'formSubmitted',
      formContent: formContent,
      formContent2: formContent2,
      formAdditionalContent: formAdditionalContent,
      formStatus: props.formStatus || null,
      formFieldCausingError: props.formFieldCausingError || null,
      formSubmitText: props.formSubmitText || null,
    }
  }, [
    getFormLength,
    props.formName,
    props.formId,
    props.formLocation,
    props.formDestination,
    props.formStatus,
    props.formFieldCausingError,
    props.formSubmitText,
    props.formContent,
    props.formContent2,
    props.formAdditionalContent,
    props.getFormContent,
    props.getFormContent2,
    props.getFormAdditionalContent,
  ])

  const submitGa = useCallback(() => {
    const model = buildModel()
    pushToDataLayer(model)
  }, [buildModel, pushToDataLayer])

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (onSubmit) {
        onSubmit(e as React.FormEvent<HTMLFormElement>)
      }

      if (!props.deferGaSubmit) {
        submitGa()
      }
    },
    [onSubmit, submitGa, props.deferGaSubmit],
  )

  return { handleSubmit, submitGa }
}
